import type { Action } from '@unifyapps/defs/types/action';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useTranslation } from '@unifyapps/i18n/client';
import { useAdaptFileUrl } from '@unifyapps/network/hooks/useAdaptFileUrl';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import { useShareAPI } from '../../../../../hooks/useShareAPI';
import { htmlToImgUrl } from '../../../../../utils/htmlToImg';
import type { OnActionArgs, OnActionType } from '../../../../context/ActionsProvider/context';
import { useSharedFileProgress } from '../../../../context/SharedFileProgressProvider';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import { BlockDataAttributes } from '../../../useBlockDataAttributes';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';
import { shareBase64Image } from './utils';

const useOpenShareDialog = () => {
  const pageEl = useContainerElementContext();
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const { setShowFileDownloadDialog } = useSharedFileProgress();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['common']);
  const adaptFileUrl = useAdaptFileUrl();
  const { shareUrl, shareFile } = useShareAPI();

  const openShareDialog: OnActionType = useEventCallback(async (args?: OnActionArgs) => {
    if (!args || !args.action || !args.action.payload) {
      // defensively handle missing args or payload
      return false;
    }

    const computedAction = getNoCodeComputedData<Action>(
      args.action as unknown as Record<string, unknown>,
      args.actionContext ?? {},
    );

    const { message, url, blockId } = computedAction.payload as {
      message: string;
      url?: string;
      blockId?: string;
    };

    const adaptedUrl = url ? adaptFileUrl(url, { preview: false }) : undefined;
    try {
      if (blockId) {
        const elToShare = pageEl?.querySelector(`[${BlockDataAttributes.BlockId}="${blockId}"]`);
        if (elToShare) {
          setShowFileDownloadDialog(true);
          const imgUrl = await htmlToImgUrl(elToShare as HTMLElement);

          // slight delay in closing the dialog in case the imgUrl is generated quickly
          await new Promise((resolve) => setTimeout(resolve, 500));
          setShowFileDownloadDialog(false);

          await shareBase64Image(imgUrl, t, shareFile);
          return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
        }
      }

      await shareUrl({
        text: message,
        url: adaptedUrl,
      });

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    } catch (err) {
      setShowFileDownloadDialog(false);

      showSnackbar({ title: err.name, description: err.message, color: 'error' });
      console.error('Something went wrong while sharing', err);
      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    }
  });

  return { openShareDialog };
};

export default useOpenShareDialog;
